import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowRight, ArrowLeft, FileText, ClipboardCheck, Info, ShieldCheck,
} from 'lucide-react';
import { PublicNavbar } from '../components/brand/PublicNavbar';
import { Footer } from '../components/brand/Footer';
import { Seo } from '../components/seo/Seo';
import {
  organizationSchema, breadcrumbSchema, faqSchema, articleSchema,
} from '../lib/seoSchemas';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ───────────────────────────────────────────────────────────────────────
   /for-families/advance-directives/:state  (Feb 2026 iter, Item #2)
   Indexable per-state advance-directive landing page. Renders from the
   public `/api/acp/reference/advance-directives?state=XX` endpoint fed
   by the 51-jurisdiction dataset in backend/data/ad_reference_states.py.

   Behavior
   • Any uppercase 2-letter code that isn't in `available_states` → 404
     (returns to /for-families) so bad URLs don't leak thin content.
   • JSON-LD emitted: MedicalOrganization, BreadcrumbList, FAQPage,
     Article. Canonical points at /for-families/advance-directives/{XX}.
   • Reference-only copy — the DECISION_SUPPORT_NOTICE disclaimer is
     surfaced verbatim; nothing on this page is legal or medical advice.
   ─────────────────────────────────────────────────────────────────── */

const _slugToCode = (raw) => {
  const s = (raw || '').toUpperCase().replace(/[^A-Z]/g, '').slice(0, 2);
  return s.length === 2 ? s : null;
};

export default function ForFamiliesAdvanceDirectiveStatePage() {
  const { state: stateParam } = useParams();
  const code = _slugToCode(stateParam);
  const [payload, setPayload] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!code) { setNotFound(true); setLoading(false); return; }
    let live = true;
    setLoading(true);
    axios.get(`${API}/acp/reference/advance-directives?state=${code}`)
      .then((res) => {
        if (!live) return;
        const avail = res.data?.available_states || [];
        if (!avail.includes(code)) { setNotFound(true); }
        setPayload(res.data);
      })
      .catch(() => { if (live) setPayload(null); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [code]);

  const ref = payload?.reference || null;
  const stateName = ref?.state_name || code || '';
  const canonicalPath = `/for-families/advance-directives/${code || ''}`;

  const faqs = useMemo(() => {
    if (!ref) return [];
    return [
      {
        q: `What advance-directive documents does ${stateName} recognize?`,
        a: `${stateName} recognizes: ${(ref.recognized_documents || []).join('; ')}.`,
      },
      {
        q: `How is an advance directive signed in ${stateName}?`,
        a: ref.signing_note,
      },
      {
        q: `When does an advance directive take effect in ${stateName}?`,
        a: ref.effective_note,
      },
      {
        q: `Where can families get the official ${stateName} forms?`,
        a: ref.official_source_hint,
      },
      {
        q: 'Does CaraLoom provide legal or medical advice?',
        a: (payload?.decision_support_notice)
          || 'No. CaraLoom provides reference content and coordination software only — never legal or medical advice.',
      },
    ];
  }, [ref, stateName, payload]);

  if (notFound || (!loading && !ref)) {
    return <Navigate to="/for-families" replace />;
  }

  const title = ref
    ? `Advance Directives in ${stateName} — What Families Need to Know | CaraLoom`
    : 'Advance Directives — CaraLoom';
  const description = ref
    ? `Plain-language reference for families in ${stateName}: recognized advance-directive documents, how they are signed, when they take effect, and where to find the official ${stateName} forms.`
    : 'Advance-directive reference for families.';

  const schemas = ref ? [
    organizationSchema(),
    breadcrumbSchema([
      { name: 'Home', path: '/' },
      { name: 'For families', path: '/for-families' },
      { name: `Advance directives — ${stateName}`, path: canonicalPath },
    ]),
    faqSchema(faqs),
    articleSchema({
      title: `Advance Directives in ${stateName}`,
      description,
      slug: `advance-directives-${(code || '').toLowerCase()}`,
      datePublished: '2026-02-01',
    }),
  ] : [];

  return (
    <div className="min-h-screen bg-cn-bg text-cn-text"
         data-testid="acp-state-page">
      <Seo
        title={title}
        description={description}
        path={canonicalPath}
        keywords={ref ? [
          `${stateName} advance directive`,
          `${stateName} healthcare proxy`,
          `${stateName} living will`,
          `${stateName} POLST MOLST`,
          'advance care planning',
          'family caregiver reference',
        ] : []}
        schema={schemas}
      />
      <PublicNavbar />

      {/* Hero */}
      <section className="border-b border-cn-border">
        <div className="cn-container pt-14 md:pt-20 pb-10 md:pb-14">
          <div className="max-w-3xl">
            <nav className="text-xs text-cn-text-muted mb-4"
                 aria-label="Breadcrumb"
                 data-testid="acp-state-breadcrumb">
              <Link to="/" className="hover:text-cn-primary">Home</Link>
              <span className="mx-2">/</span>
              <Link to="/for-families" className="hover:text-cn-primary">
                For families
              </Link>
              <span className="mx-2">/</span>
              <span>Advance directives — {stateName}</span>
            </nav>
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-cn-bone-200/80 dark:bg-white/5 border border-cn-border mb-6"
                 data-testid="acp-state-badge">
              <FileText className="w-3.5 h-3.5 text-cn-primary" aria-hidden="true" />
              <span className="text-xs font-semibold tracking-wide uppercase text-cn-text">
                Reference &middot; {code}
              </span>
            </div>
            <h1 className="cn-display text-[2.4rem] sm:text-[3rem] lg:text-[3.4rem] leading-[1.05] tracking-tight"
                data-testid="acp-state-h1">
              Advance directives in {stateName}
            </h1>
            <p className="mt-5 text-lg text-cn-text-muted max-w-2xl"
               data-testid="acp-state-lede">
              A plain-language reference for families and caregivers.
              Not legal or medical advice — the forms and rules linked
              below are the {stateName} sources of truth.
            </p>
          </div>
        </div>
      </section>

      {loading && (
        <section className="cn-container py-16" data-testid="acp-state-loading">
          <p className="text-cn-text-muted">Loading {stateName}…</p>
        </section>
      )}

      {!loading && ref && (
        <>
          {/* Recognized documents */}
          <section className="cn-container py-14 md:py-16">
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight"
                data-testid="acp-state-docs-heading">
              Documents recognized in {stateName}
            </h2>
            <ul className="mt-6 grid sm:grid-cols-2 gap-3"
                data-testid="acp-state-docs-list">
              {(ref.recognized_documents || []).map((doc, i) => (
                <li key={i}
                    className="cn-card rounded-2xl p-5 flex items-start gap-3"
                    data-testid={`acp-state-doc-${i}`}>
                  <ClipboardCheck className="w-5 h-5 mt-0.5 shrink-0 text-cn-primary" aria-hidden="true" />
                  <span className="text-sm">{doc}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Signing */}
          <section className="cn-container pb-14 md:pb-16"
                   data-testid="acp-state-signing">
            <div className="cn-card rounded-2xl p-6 md:p-8">
              <h2 className="text-xl md:text-2xl font-semibold tracking-tight">
                How it&rsquo;s signed
              </h2>
              <p className="mt-3 text-cn-text-muted leading-relaxed">
                {ref.signing_note}
              </p>
            </div>
          </section>

          {/* Effective */}
          <section className="cn-container pb-14 md:pb-16"
                   data-testid="acp-state-effective">
            <div className="cn-card rounded-2xl p-6 md:p-8">
              <h2 className="text-xl md:text-2xl font-semibold tracking-tight">
                When it takes effect
              </h2>
              <p className="mt-3 text-cn-text-muted leading-relaxed">
                {ref.effective_note}
              </p>
              <p className="mt-4 text-xs text-cn-text-muted italic">
                <Info className="w-3 h-3 inline mr-1 -mt-0.5" aria-hidden="true" />
                Signing and taking effect are not the same moment for
                every document.
              </p>
            </div>
          </section>

          {/* Official forms */}
          <section className="cn-container pb-14 md:pb-16"
                   data-testid="acp-state-source">
            <div className="cn-card rounded-2xl p-6 md:p-8">
              <h2 className="text-xl md:text-2xl font-semibold tracking-tight">
                Where to find the {stateName} forms
              </h2>
              <p className="mt-3 text-cn-text-muted leading-relaxed">
                {ref.official_source_hint}
              </p>
              <p className="mt-4 text-xs text-cn-text-muted">
                <ShieldCheck className="w-3 h-3 inline mr-1 -mt-0.5" aria-hidden="true" />
                Requirements change. Always verify against your
                state&rsquo;s current statutory form before relying on
                any summary — including this one.
              </p>
            </div>
          </section>

          {/* FAQ */}
          <section className="cn-container pb-14 md:pb-16"
                   data-testid="acp-state-faq">
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight">
              Frequently asked in {stateName}
            </h2>
            <div className="mt-6 space-y-3">
              {faqs.map((f, i) => (
                <details key={i}
                         className="cn-card rounded-2xl p-5"
                         data-testid={`acp-state-faq-${i}`}>
                  <summary className="cursor-pointer font-semibold">
                    {f.q}
                  </summary>
                  <p className="mt-3 text-sm text-cn-text-muted leading-relaxed">
                    {f.a}
                  </p>
                </details>
              ))}
            </div>
          </section>

          {/* Other states + CTA */}
          <section className="cn-container pb-20"
                   data-testid="acp-state-related">
            <div className="cn-card rounded-2xl p-6 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <h2 className="text-xl font-semibold tracking-tight">
                  Different state?
                </h2>
                <p className="mt-2 text-sm text-cn-text-muted max-w-md">
                  We publish plain-language references for every U.S.
                  state and the District of Columbia. Return to the
                  families hub to pick another.
                </p>
              </div>
              <Link
                to="/for-families"
                className="cn-btn inline-flex items-center gap-2 self-start"
                data-testid="acp-state-back-cta">
                <ArrowLeft className="w-4 h-4" />
                All state references
              </Link>
            </div>

            <div className="mt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6"
                 data-testid="acp-state-planner-cta">
              <p className="text-sm text-cn-text-muted max-w-2xl">
                CaraLoom is care-coordination software for hospitals and
                receiving providers. If your family member is being
                discharged, the discharge planner can share a read-only
                status view with you so you don&rsquo;t have to chase
                updates on the phone.
              </p>
              <Link
                to="/for-families#status-view"
                className="cn-btn cn-btn-primary inline-flex items-center gap-2"
                data-testid="acp-state-status-cta">
                Learn about the status view
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </section>
        </>
      )}

      <Footer />
    </div>
  );
}
